<template>
  <HgModal
    v-model="isOpen"
    data-testid="regional-highlight-purchase-modal"
    :containerCssClass="$style.modalContainer"
    @close="handleClose"
  >
    <template #title>{{ t('regionalHighlight.purchaseModal.title') }}</template>

    <div v-if="optionsLoading" :class="$style.loading">
      <HgSpinner size="large" />
    </div>

    <div
      v-else-if="optionsLoadFailed"
      :class="$style.optionsError"
      data-testid="regional-highlight-purchase-options-error"
    >
      <HgNotificationMessage theme="danger" :title="t('application.error.title')">
        {{ t('regionalHighlight.purchaseModal.errors.optionsLoadFailed') }}
      </HgNotificationMessage>
      <HgButton
        theme="primary-bordered"
        size="small"
        data-testid="regional-highlight-purchase-options-retry"
        @click="loadPurchaseOptions"
      >
        {{ t('performanceDetails.nba.retry') }}
      </HgButton>
    </div>

    <template v-else>
      <p :class="$style.intro">{{ t('regionalHighlight.purchaseModal.intro') }}</p>
      <hr :class="$style.divider" />

      <HgNotificationMessage
        v-if="errorMessage"
        theme="danger"
        :title="t('application.error.title')"
        :class="$style.errorBanner"
        data-testid="regional-highlight-purchase-error"
      >
        {{ errorMessage }}
      </HgNotificationMessage>

      <div :class="$style.columns">
        <div :class="$style.inputs">
          <HgSelect
            v-model="selectedDistrictId"
            name="regional-highlight-purchase-region"
            :label="t('regionalHighlight.purchaseModal.regionLabel')"
            :options="regionSelectOptions"
            data-testid="regional-highlight-purchase-region"
          >
            <template #helperText>
              <p v-if="selectedOption">
                <strong>{{ positionsFreeLabelForOption(selectedOption) }}</strong>
                <template v-if="regionHelperSearchesText">
                  <span> · {{ regionHelperSearchesText }}</span>
                </template>
              </p>
            </template>
          </HgSelect>

          <RegionalHighlightPurchaseBookingPeriod
            v-model:durationMonths="durationMonths"
            v-model:contractStartDate="contractStartDate"
            :durationOptions="durationOptions"
            :minContractStartDate="minContractStartDate"
            :maxContractStartDate="maxContractStartDate"
          />
        </div>

        <div :class="$style.summary">
          <span :class="$style.summaryHeading">{{
            t('regionalHighlight.purchaseModal.summaryHeading')
          }}</span>

          <div :class="$style.summaryRow">
            <span>{{ t('regionalHighlight.purchaseModal.summaryRegion') }}</span>
            <span data-testid="regional-highlight-purchase-summary-region">{{
              summaryRegionLabel
            }}</span>
          </div>
          <div :class="$style.summaryRow">
            <span>{{ t('regionalHighlight.purchaseModal.summaryMonthlyPrice') }}</span>
            <span data-testid="regional-highlight-purchase-summary-monthly">{{
              summaryMonthlyPriceLabel
            }}</span>
          </div>
          <div :class="$style.summaryRow">
            <span>{{ t('regionalHighlight.purchaseModal.summaryPeriod') }}</span>
            <span data-testid="regional-highlight-purchase-summary-period">{{
              summaryPeriodLabel
            }}</span>
          </div>
          <div :class="$style.summaryRow">
            <span>{{ t('regionalHighlight.purchaseModal.summaryBilledMonths') }}</span>
            <span data-testid="regional-highlight-purchase-summary-billed">{{
              summaryBilledMonthsLabel
            }}</span>
          </div>
          <div :class="$style.summaryTotal">
            <span>{{ t('regionalHighlight.purchaseModal.summaryTotal') }}</span>
            <span data-testid="regional-highlight-purchase-summary-total">{{
              summaryTotalLabel
            }}</span>
          </div>

          <HgNotificationMessage
            theme="info"
            :title="t('regionalHighlight.purchaseModal.confirmInfoTitle')"
          >
            {{ t('regionalHighlight.purchaseModal.confirmInfoBody') }}
          </HgNotificationMessage>
        </div>
      </div>
    </template>

    <template #footer>
      <HgButton
        theme="primary-bordered"
        size="small"
        :disabled="confirmInFlight"
        @click="handleClose"
      >
        {{ t('general.buttons.cancel') }}
      </HgButton>
      <HgButton
        theme="primary"
        size="small"
        :disabled="!canConfirm"
        data-testid="regional-highlight-purchase-confirm"
        @click="handleConfirm"
      >
        {{ t('regionalHighlight.purchaseModal.confirmBooking') }}
      </HgButton>
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import { ModalIds } from '@/components/ModalManager/types';
import { isHgRetsDatePresentOrFuture } from '@/helpers/dateUtils';
import { formatIntegerWithThousandSeparator } from '@/helpers/numberUtils';
import { useAppStore } from '@/stores/app/useAppStore';
import { useToplistingStore } from '@/stores/toplisting/useToplistingStore';
import { ToastTheme } from '@/stores/types/app';
import { ApiCallStatus } from '@/stores/types/insertions';
import type {
  RegionalHighlightPurchaseDurationMonths,
  RegionalHighlightPurchaseOption,
} from '@/types/regionalHighlight';
import {
  HgButton,
  HgModal,
  HgNotificationMessage,
  HgSelect,
  HgSpinner,
  type HgSelectOptions,
} from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import RegionalHighlightPurchaseBookingPeriod from './RegionalHighlightPurchaseBookingPeriod.vue';
import { regionalHighlightPurchaseDistrictLabel } from './regionalHighlightPurchaseDistrictLabel';
import {
  readRegionalHighlightPurchaseErrorStatusCode,
  resolveRegionalHighlightPurchaseConfirmErrorMessage,
} from './regionalHighlightPurchaseModalErrors';
import {
  regionalHighlightPurchasePositionsFreeLabel,
  regionalHighlightPurchaseRegionOptionLabel,
} from './regionalHighlightPurchaseRegionOptionLabel';
import {
  computeTermTotalChfCents,
  formatChfWholeFrancsFromCents,
  formatFrancsFromChfCents,
  formatPurchaseSummaryPeriod,
  getEarliestSelectableContractStartDate,
  getLatestSelectableContractStartDate,
  isFirstOfMonthHgRetsDate,
} from './regionalHighlightPurchaseSummary';

const DURATION_VALUES: RegionalHighlightPurchaseDurationMonths[] = [1, 3, 6, 12];
const EMPTY_PURCHASE_OPTIONS: RegionalHighlightPurchaseOption[] = [];

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: boolean): void;
}>();

const { t, locale } = useI18n();
const appStore = useAppStore();
const toplistingStore = useToplistingStore();

const selectedDistrictId = ref('');
const durationMonths = ref<RegionalHighlightPurchaseDurationMonths>(3);
const minContractStartDate = ref(getEarliestSelectableContractStartDate());
const maxContractStartDate = ref(getLatestSelectableContractStartDate());
const contractStartDate = ref(minContractStartDate.value);
const modalSessionId = ref(0);
const errorMessage = ref('');
const idempotencyKey = ref<string | undefined>();
const idempotencyInputsKey = ref('');
const isSubmitting = ref(false);
const activeConfirmSessionId = ref<number | null>(null);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => {
    if (!value && confirmInFlight.value) {
      return;
    }
    emit('update:modelValue', value);
  },
});

const apiStatus = computed(() => toplistingStore.regionalHighlightApiStatus);
const purchaseOptions = computed(
  () => toplistingStore.regionalHighlightPurchaseOptions?.options ?? EMPTY_PURCHASE_OPTIONS,
);

const optionsLoading = computed(
  () => apiStatus.value?.purchaseOptions === ApiCallStatus.IN_PROGRESS,
);

const optionsLoadFailed = computed(() => apiStatus.value?.purchaseOptions === ApiCallStatus.ERROR);

const confirmInFlight = computed(() => {
  if (isSubmitting.value) {
    return true;
  }
  if (apiStatus.value?.createPurchaseOrder !== ApiCallStatus.IN_PROGRESS) {
    return false;
  }
  return activeConfirmSessionId.value === modalSessionId.value;
});

const isDurationPriceAvailable = (months: RegionalHighlightPurchaseDurationMonths): boolean =>
  selectedOption.value?.monthlyPriceByDuration[months] != null;

const durationOptions = computed(() =>
  DURATION_VALUES.map((months) => ({
    value: String(months),
    text: t(`regionalHighlight.purchaseModal.duration${months}Months`),
    disabled: Boolean(selectedOption.value) && !isDurationPriceAvailable(months),
  })),
);

const ensureDurationHasPrice = (): void => {
  if (!selectedOption.value || isDurationPriceAvailable(durationMonths.value)) {
    return;
  }
  const supported = DURATION_VALUES.find((months) => isDurationPriceAvailable(months));
  if (supported !== undefined) {
    durationMonths.value = supported;
  }
};

const districtLabelForOption = (option: RegionalHighlightPurchaseOption): string =>
  regionalHighlightPurchaseDistrictLabel(option, locale.value);

const positionsFreeLabelForOption = (option: RegionalHighlightPurchaseOption): string =>
  regionalHighlightPurchasePositionsFreeLabel(option, t);

const sortedPurchaseOptions = computed(() =>
  [...purchaseOptions.value].sort((left, right) =>
    districtLabelForOption(left).localeCompare(districtLabelForOption(right), locale.value),
  ),
);

const regionSelectOptions = computed<HgSelectOptions[]>(() =>
  sortedPurchaseOptions.value.map((option: RegionalHighlightPurchaseOption) => {
    const isSelected = option.districtId === selectedDistrictId.value;
    return {
      value: option.districtId,
      text: regionalHighlightPurchaseRegionOptionLabel(option, {
        locale: locale.value,
        durationMonths: durationMonths.value,
        includePositionsInLabel: !isSelected,
        t,
        formatFrancsFromChfCents,
      }),
      disabled: !option.purchasable,
    };
  }),
);

const selectedOption = computed(() =>
  purchaseOptions.value.find(
    (option: RegionalHighlightPurchaseOption) => option.districtId === selectedDistrictId.value,
  ),
);

const regionHelperSearchesText = computed(() => {
  const option = selectedOption.value;
  if (!option || option.searchesPerMonth <= 0) {
    return '';
  }
  return t('regionalHighlight.district.searchesPerMonth', {
    count: formatIntegerWithThousandSeparator(option.searchesPerMonth, locale.value),
  });
});

const summaryRegionLabel = computed(() =>
  selectedOption.value ? districtLabelForOption(selectedOption.value) : '—',
);

const summaryMonthlyPriceLabel = computed(() => {
  const cents = selectedOption.value?.monthlyPriceByDuration[durationMonths.value];
  return cents != null ? formatChfWholeFrancsFromCents(cents, locale.value) : '—';
});

const summaryPeriodLabel = computed(() =>
  formatPurchaseSummaryPeriod(contractStartDate.value, durationMonths.value, locale.value),
);

const isContractStartDateValid = computed(
  () =>
    isFirstOfMonthHgRetsDate(contractStartDate.value) &&
    isHgRetsDatePresentOrFuture(contractStartDate.value) &&
    contractStartDate.value >= minContractStartDate.value &&
    contractStartDate.value <= maxContractStartDate.value,
);

const summaryBilledMonthsLabel = computed(() =>
  t(`regionalHighlight.purchaseModal.billedMonths${durationMonths.value}`),
);

const summaryTotalLabel = computed(() => {
  const monthlyCents = selectedOption.value?.monthlyPriceByDuration[durationMonths.value];
  if (monthlyCents == null) {
    return '—';
  }
  const totalCents = computeTermTotalChfCents(monthlyCents, durationMonths.value);
  return formatChfWholeFrancsFromCents(totalCents, locale.value);
});

const canConfirm = computed(
  () =>
    Boolean(selectedOption.value?.purchasable) &&
    !optionsLoading.value &&
    !optionsLoadFailed.value &&
    !confirmInFlight.value &&
    isContractStartDateValid.value &&
    selectedOption.value?.monthlyPriceByDuration[durationMonths.value] != null,
);

const resetIdempotencyForNewModalSession = (): void => {
  idempotencyKey.value = undefined;
  idempotencyInputsKey.value = '';
};

const syncIdempotencyInputsKey = (): void => {
  const nextInputsKey = `${selectedDistrictId.value}:${durationMonths.value}:${contractStartDate.value}`;
  if (idempotencyInputsKey.value === nextInputsKey) {
    return;
  }
  idempotencyInputsKey.value = nextInputsKey;
  idempotencyKey.value = undefined;
};

watch([selectedDistrictId, durationMonths, contractStartDate], syncIdempotencyInputsKey);

watch(selectedOption, () => {
  if (!props.modelValue) {
    return;
  }
  ensureDurationHasPrice();
});

watch(durationMonths, () => {
  ensureDurationHasPrice();
});

const selectFirstPurchasableDistrict = (): void => {
  const first = sortedPurchaseOptions.value.find(
    (option: RegionalHighlightPurchaseOption) => option.purchasable,
  );
  selectedDistrictId.value = first?.districtId ?? sortedPurchaseOptions.value[0]?.districtId ?? '';
};

const loadPurchaseOptions = async (): Promise<void> => {
  await toplistingStore.fetchRegionalHighlightPurchaseOptions();
  if (optionsLoadFailed.value) {
    return;
  }
  selectFirstPurchasableDistrict();
  ensureDurationHasPrice();
};

watch(
  () => props.modelValue,
  async (open) => {
    if (!open) {
      return;
    }
    modalSessionId.value += 1;
    errorMessage.value = '';
    durationMonths.value = 3;
    const earliest = getEarliestSelectableContractStartDate();
    const latest = getLatestSelectableContractStartDate();
    minContractStartDate.value = earliest;
    maxContractStartDate.value = latest;
    contractStartDate.value = earliest;
    resetIdempotencyForNewModalSession();
    await loadPurchaseOptions();
  },
  { immediate: true },
);

watch(
  () => purchaseOptions.value,
  (options) => {
    if (!props.modelValue || options.length === 0) {
      return;
    }
    if (
      !options.some(
        (option: RegionalHighlightPurchaseOption) => option.districtId === selectedDistrictId.value,
      )
    ) {
      selectFirstPurchasableDistrict();
    }
    ensureDurationHasPrice();
  },
  { immediate: true },
);

const handleClose = (): void => {
  if (confirmInFlight.value) {
    return;
  }
  emit('update:modelValue', false);
  appStore.closeModal({ id: ModalIds.RegionalHighlightPurchaseModal });
};

const handleConfirmFailure = async (error: unknown): Promise<void> => {
  errorMessage.value = resolveRegionalHighlightPurchaseConfirmErrorMessage(error, {
    districtSoldOut: t('regionalHighlight.purchaseModal.errors.districtSoldOut'),
    generic: t('regionalHighlight.purchaseModal.errors.generic'),
  });
  if (readRegionalHighlightPurchaseErrorStatusCode(error) === 409) {
    await toplistingStore.fetchRegionalHighlightPurchaseOptions();
    selectFirstPurchasableDistrict();
  }
};

const handleConfirmSuccess = (): void => {
  appStore.showToast({
    title: t('regionalHighlight.purchaseModal.toast.title'),
    message: t('regionalHighlight.purchaseModal.toast.message'),
    theme: ToastTheme.Info,
  });
  handleClose();
};

const handleConfirm = async (): Promise<void> => {
  if (!canConfirm.value || !selectedOption.value) {
    return;
  }

  if (!idempotencyKey.value) {
    idempotencyKey.value = crypto.randomUUID();
  }
  errorMessage.value = '';
  isSubmitting.value = true;
  activeConfirmSessionId.value = modalSessionId.value;
  const submissionSessionId = modalSessionId.value;

  try {
    await toplistingStore.createRegionalHighlightPurchaseOrder({
      idempotencyKey: idempotencyKey.value,
      districtId: selectedDistrictId.value,
      durationMonths: durationMonths.value,
      contractStartDate: contractStartDate.value,
      locale: locale.value,
    });
  } finally {
    isSubmitting.value = false;
    activeConfirmSessionId.value = null;
  }

  if (submissionSessionId !== modalSessionId.value || !props.modelValue) {
    return;
  }

  const status = toplistingStore.regionalHighlightApiStatus?.createPurchaseOrder;
  if (status === ApiCallStatus.SUCCESS) {
    handleConfirmSuccess();
    return;
  }

  await handleConfirmFailure(toplistingStore.regionalHighlightErrors?.createPurchaseOrder);
};
</script>

<style lang="scss" module>
@import '@/assets/variables';

$modal-width: 48.75rem; // 780px — overrides Domus dialog default max-width (680px)

.modalContainer {
  &[class*='Modal_dialog'] {
    width: calc(100% - $space-150 * 2);
    max-width: $modal-width;

    @media (min-width: $viewport-sm-min) {
      width: min($modal-width, calc(100vw - $space-300));
      max-width: $modal-width;
    }
  }
}

.loading {
  display: flex;
  justify-content: center;
  padding: $space-200 0;
}

.intro {
  @include body-2;

  margin: 0 0 $space-100;
  color: $color-deep-blue-100;
}

.divider {
  margin: 0 0 $space-150;
  border: 0;
  border-top: 1px solid $color-deep-blue-20;
}

.errorBanner {
  margin-bottom: $space-100;
}

.optionsError {
  display: flex;
  flex-direction: column;
  gap: $space-100;
}

.columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $space-200;

  @media (width <= 48rem) {
    grid-template-columns: 1fr;
  }
}

.inputs {
  display: flex;
  flex-direction: column;
  gap: $space-150;
}

.summary {
  display: flex;
  flex-direction: column;
  gap: $space-75;
}

.summaryHeading {
  @include body-2-bold;

  color: $color-deep-blue-100;
}

.summaryRow {
  @include body-2;

  display: flex;
  gap: $space-100;
  justify-content: space-between;
  color: $color-deep-blue-100;
}

.summaryTotal {
  @include body-2-bold;

  display: flex;
  gap: $space-100;
  justify-content: space-between;
  padding: $space-50 $space-50;
  margin-bottom: $space-50;
  color: $color-deep-blue-100;
  background-color: $color-deep-blue-0;
  border-radius: $space-25;
}
</style>
